import classNames from 'classnames/bind';
import React, { useContext, useRef, useMemo, useState } from 'react';

import styles from './index.module.scss';

import { FunctionButton } from '@/components/ui/kit/Button';
import { FieldNumber, FieldText } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import { MOUNT_POINTS } from '@/define/mountPoints';
import { NTRIPContext } from '@/helpers/NTRIPProvider';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const DEFAULT_OPTIONS = {
  host: 'www.gnssdata.or.kr',
  port: '2101',
  username: '',
  password: '',
  mountPoint: '',
};

const Ntrip = ({ robot }) => {
  const ntrip = useContext(NTRIPContext);
  const domRefs = useRef({
    host: useRef(),
    port: useRef(),
    username: useRef(),
    password: useRef(),
  }).current;

  const isConnecting = useMemo(() => {
    return ntrip.robotOptions.allIds.includes(robot.id);
  }, [ntrip.robotOptions]);

  const options = useMemo(() => {
    return ntrip.robotOptions.byId[robot.id] || DEFAULT_OPTIONS;
  }, [ntrip.robotOptions]);

  const [mountPoint, setMountPoint] = useState(options.mountPoint);

  const validAll = () => {
    const host = domRefs.host.current;
    const port = domRefs.port.current;
    const username = domRefs.username.current;
    const password = domRefs.password.current;

    if (host.value.trim() === '') {
      notifier.error('Please enter the host.');
      host.focus();
      return false;
    }
    if (port.value.trim() === '') {
      notifier.error('Please enter the port.');
      port.focus();
      return false;
    }
    if (username.value.trim() === '') {
      notifier.error('Please enter the username.');
      username.focus();
      return false;
    }
    if (password.value.trim() === '') {
      notifier.error('Please enter the password.');
      password.focus();
      return false;
    }
    if (mountPoint === '') {
      notifier.error('Please select the mount point.');
      return false;
    }
    return true;
  };

  const doConnect = () => {
    if (!validAll()) return;

    const options = {
      host: domRefs.host.current.value,
      port: domRefs.port.current.value,
      username: domRefs.username.current.value,
      password: domRefs.password.current.value,
      mountPoint,
    };
    ntrip.connect(robot.id, options);
  };

  const doDisconnect = () => {
    ntrip.disconnect(robot.id);
  };

  const handleChange = (e) => {
    setMountPoint(e.target.value);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('rows')}>
        <div className={cx('row')}>
          <div className={cx('label')}>Host</div>
          <FieldText ref={domRefs.host} name="host" defaultValue={options.host} readOnly={isConnecting} />
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Port</div>
          <FieldNumber ref={domRefs.port} name="port" defaultValue={options.port} readOnly={isConnecting} />
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Username</div>
          <FieldText ref={domRefs.username} name="username" defaultValue={options.username} readOnly={isConnecting} />
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Password</div>
          <FieldText
            ref={domRefs.password}
            name="password"
            type="password"
            defaultValue={options.password}
            readOnly={isConnecting}
          />
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Mount Point</div>
          <Select value={mountPoint} disabled={isConnecting} onChange={handleChange}>
            {MOUNT_POINTS.map(({ rtcmTypes, name }) =>
              rtcmTypes.map((rtcmType, index) => (
                <option key={index} value={rtcmType}>
                  {name} | {rtcmType}
                </option>
              )),
            )}
          </Select>
        </div>
      </div>
      <div>
        {!isConnecting && <FunctionButton text="Connect" onClick={doConnect} disabled={isConnecting} />}
        {isConnecting && <FunctionButton text="Disconnect" onClick={doDisconnect} disabled={!isConnecting} />}
      </div>
    </div>
  );
};

export default Ntrip;
